import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { RouterProvider } from 'react-aria-components';
import { Navigate, Outlet, Route, Routes, useHref, useLocation, useNavigate } from 'react-router';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, FailureText, PageHeader } from '@ui/console/primitives';
import { TextField } from '@ui/console/forms';
import { accessToken, SESSION_EXPIRED_EVENT, signIn } from './auth';
import { ForgotPassword, ResetPassword } from './AuthRoutes';
import { Actions } from './components';
import { Shell } from './Shell';
import { OutfitReviewScreen } from './OutfitReview';
import { ReviewQueueScreen } from './ReviewQueue';
import { MemberPage } from './MemberPage';
import { MyMembers } from './MyMembers';
import { NotFound } from './screens';

/**
 * The design system's links are React Aria links, so their `href` has to be
 * routed through react-router or every in-app link would reload the document.
 */
function AriaRouting({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  return (
    <RouterProvider navigate={(to) => void navigate(to)} useHref={useHref}>
      {children}
    </RouterProvider>
  );
}

function RequireStylist() {
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState<'live' | 'absent' | 'expired'>(() =>
    accessToken() ? 'live' : 'absent',
  );
  useEffect(() => {
    const expire = () => {
      setSession('expired');
      void navigate('/sign-in', { replace: true, state: { from: location, expired: true } });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, expire);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, expire);
  }, [location, navigate]);
  return session === 'live' ? (
    <Outlet />
  ) : (
    <Navigate to="/sign-in" state={{ from: location, expired: session === 'expired' }} replace />
  );
}

function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const expired = Boolean((location.state as { expired?: boolean } | null)?.expired);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = form.get('email');
    const password = form.get('password');
    setLoading(true);
    setError(undefined);
    try {
      await signIn(
        typeof email === 'string' ? email : '',
        typeof password === 'string' ? password : '',
      );
      const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
      void navigate(from && from !== '/sign-in' ? from : '/', { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Sign-in failed.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="flex min-h-dvh items-center justify-center bg-secondary px-4 py-10">
      <div className="flex w-full max-w-md flex-col gap-6">
        <a href="/" className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-solid text-sm font-bold text-white">
            CT
          </span>
          <span className="flex min-w-0 flex-col leading-tight">
            <strong className="truncate text-sm font-semibold text-primary">Closet Theory</strong>
            <span className="truncate text-xs text-tertiary">Staff access</span>
          </span>
        </a>
        <PageHeader
          title="Stylist sign in"
          description="Use the stylist account assigned to you by an administrator."
        />
        <form
          onSubmit={(event) => void submit(event)}
          className="flex flex-col gap-4 rounded-xl border border-secondary bg-primary p-5"
        >
          <TextField label="Email" name="email" type="email" autoComplete="username" required />
          <TextField
            label="Password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
          />
          {error ? (
            <Callout tone="error" title="Sign-in failed">
              <FailureText raw={error} />
            </Callout>
          ) : expired ? (
            <Callout tone="warning" title="Your session expired. Sign in again to carry on." />
          ) : null}
          <Actions>
            <Button size="sm" color="link-gray" href="/forgot-password">
              Forgot password?
            </Button>
            <Button type="submit" size="sm" isLoading={loading}>
              Sign in to assigned tasks
            </Button>
          </Actions>
        </form>
      </div>
    </div>
  );
}

export function App() {
  return (
    <AriaRouting>
      <Routes>
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route element={<RequireStylist />}>
          <Route element={<Shell />}>
            <Route index element={<MyMembers />} />
            <Route path="review" element={<ReviewQueueScreen />} />
            <Route path="review/:outfitId" element={<OutfitReviewScreen />} />
            {/* Retired pages; old bookmarks land on the members list. */}
            <Route path="look-requests" element={<Navigate to="/" replace />} />
            <Route path="approvals" element={<Navigate to="/" replace />} />
            <Route path="clients/:clientId" element={<MemberPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Routes>
    </AriaRouting>
  );
}
